import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, message } from 'antd';
import { FD210, pwd210Api } from '../../services/pwd210Api';
import { BatchAcceptanceBridge, pwd210f1AcceptanceManifest } from '../../features/acceptance';
import { usePwd210Mode } from './hooks/usePwd210Mode';
import { usePwd210Lookup } from './hooks/usePwd210Lookup';
import { usePwd210Record } from './hooks/usePwd210Record';
import { usePwd210Navigation } from './hooks/usePwd210Navigation';
import { useModulePermission } from '../../hooks/usePermission';
import { Modules } from '../../constants/permissions';
import PermissionGuard from '../../components/PermissionGuard';
import Pwd210MasterFields from './components/Pwd210MasterFields';
import Pwd210DetailTable from './components/Pwd210DetailTable';
import Pwd210ActionBar from './components/Pwd210ActionBar';
import { dateToInput, inputToDate, isFieldDisabledFn } from './helpers';
import Pwd210SourcePicker from './components/Pwd210SourcePicker';
import './Pwd210Form.css';
const acceptanceActionIds = {
  list: 'PWD210F1-ACT-LIST',
  create: 'PWD210F1-ACT-CREATE',
  update: 'PWD210F1-ACT-UPDATE',
  delete: 'PWD210F1-ACT-DELETE',
};
void acceptanceActionIds;

// ── Component ────────────────────────────────────────────────
const Pwd210Form: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [sourceOpen, setSourceOpen] = useState(false);

  // ── Hooks ──
  const perms = useModulePermission(Modules.PWD210);
  const { mode, setMode, isEditing } = usePwd210Mode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = usePwd210Lookup();
  const {
    record, formData, details, selectedRow, loading,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult, startFromSource,
    getFieldError, clearFieldErrors,
  } = usePwd210Record();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (id: string) => {
    const result = await loadRecord(id);
    if (result) {
      resolveAllNames(result.data);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = usePwd210Navigation(loadRecordWithNames);

  // ── Initial Load ──
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load pwd210 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList, loadRecordWithNames, setCurrentIdx]);

  // ── Mode Actions ──
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      // Save
      await handleSave(mode, async (result: FD210) => {
        applyResult(result);
        setMode('browse');
        if (mode === 'add') {
          try {
            const ids = await loadList();
            setCurrentIdx(ids.indexOf(result.fd210_01));
          } catch {
            message.warning('已儲存成功；清單更新失敗，請重新整理畫面');
          }
        }
      });
    } else {
      // Select the existing source before entering add mode.
      setSourceOpen(true);
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      // Revert
      revertToRecord();
      setMode('browse');
      if (record) resolveAllNames(record);
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode, resolveAllNames]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除入庫單 ${record.fd210_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecordWithNames(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  const handlePrint = useCallback(() => {
    if (!record || isEditing) return;
    message.info('列印功能開發中');
  }, [record, isEditing]);

  const isFieldDisabled = useCallback((key: string) => isFieldDisabledFn(key, isEditing), [isEditing]);

  // ── Render ──
  return (
    <PermissionGuard module={Modules.PWD210} action="query">
    <div className="pwd210-form">
      {sourceOpen && <Pwd210SourcePicker onCancel={() => setSourceOpen(false)} onSelect={draft => {
        startFromSource(draft); setMode('add'); setSourceOpen(false); setActiveTab(0);
        clearNames(); resolveAllNames(draft);
      }} />}
      {loading && <div className="loading-overlay">載入中...</div>}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <span className="current">驗布指示單</span>
            </nav>
            <span className="title-separator">—</span>
            <h1>驗布指示單（入庫單）</h1>
            <span className="badge badge-default">D210</span>
          </div>
        </div>

        <p role="note" className="text-muted">本期不提供驗布留言功能；歷史留言紀錄保留，後續版本補齊。</p>

        {/* ===== Form Info Bar (入庫單號 / 入單日期 / 入單人員 / 入庫日期) ===== */}
        <div className="form-info-bar">
          <div className="field-group">
            <label data-contract-field="fd210_01">入庫單號</label>
            <input
              type="text"
              readOnly
              style={{ width: 100 }}
              value={getField('fd210_01') as string} data-contract-field="fd210_01"
            />
          </div>
          <div className="field-group">
            <label data-contract-field="fd210_11">入單日期</label>
            <input
              type="date"
              disabled={mode === 'add' || isFieldDisabled('fd210_11')}
              value={dateToInput(getField('fd210_11') as string | null)}
              onChange={e => setField('fd210_11', inputToDate(e.target.value) as string)} data-contract-field="fd210_11"
            />
          </div>
          <div className="field-group">
            <label data-contract-field="fd210_12">入單人員</label>
            <input
              type="text"
              readOnly
              style={{ width: 80 }}
              value={getField('fd210_12') as string}
            />
            <span style={{ color: '#999', fontSize: 12 }}>{names.entryPerson || ''}</span>
          </div>
          <div className="field-group">
            <label data-contract-field="ui_receipt_date">入庫日期</label>
            <input
              type="text"
              readOnly
              style={{ width: 100 }}
              value=""
              placeholder="(由系統帶入)"
            />
          </div>
        </div>

        {/* ===== Tabs ===== */}
        <div className="tabs">
          <div className="tab-nav">
            {['驗布說明', '入庫明細'].map((label, i) => (
              <button
                key={i}
                className={`tab-nav-item ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Tab 1: 驗布說明 */}
          <div className={`tab-pane ${activeTab === 0 ? 'active' : ''}`}>
            <Pwd210MasterFields
              formData={formData}
              names={names}
              isEditing={isEditing}
              isFieldDisabled={isFieldDisabled}
              getField={getField}
              setField={setField}
              resolveSingleName={resolveSingleName}
              getFieldError={getFieldError}
            />
          </div>

          {/* Tab 2: 入庫明細 */}
          <div className={`tab-pane ${activeTab === 1 ? 'active' : ''}`}>
            <Pwd210DetailTable
              details={details}
              selectedRow={selectedRow}
              isEditing={isEditing}
              totalItems={formData.fd210_21 || details.length}
              setSelectedRow={setSelectedRow}
              getDetailField={getDetailField}
              setDetailField={setDetailField}
              addDetailRow={addDetailRow}
              deleteDetailRow={deleteDetailRow}
            />
          </div>
        </div>{/* /tabs */}

        {/* ===== Bottom Action Bar (Pwtcmd1) ===== */}
        <Pwd210ActionBar
        busy={loading || sourceOpen}
          mode={mode}
          isEditing={isEditing}
          record={record}
          currentIdx={currentIdx}
          allIdsLength={allIds.length}
          permissions={perms}
          onAdd={handleAddAction}
          onModify={handleModifyAction}
          onDelete={handleDeleteAction}
          onPrint={handlePrint}
          onNavTo={handleNavTo}
          onExit={() => navigate('/erp')}
        />
        <BatchAcceptanceBridge
          manifest={pwd210f1AcceptanceManifest}
          records={record ? [record] : []}
          selectedKeys={record ? [record.fd210_01] : []}
          keyOf={(item) => item.fd210_01}
          queryExact={(key) => pwd210Api.get(key)}
          lastApiResponse={record}
          loading={loading}
          onAutoFill={() => {
            const first = record?.fd210_01 || allIds[0];
            if (first) loadRecordWithNames(first);
          }}
          autoFillDisabled={!record && allIds.length === 0}
          autoFillReason="先載入一筆既有不良原因說明單，驗收模式會沿用真實 API 資料。"
          screenDraft={{ master: formData, details }}
        />

      </div>{/* /page-wrapper */}
    </div>
    </PermissionGuard>
  );
};

export default Pwd210Form;
